import { createApp } from 'vue';
import { createRouter, createWebHistory } from 'vue-router';
import routes from './routes.js';
import { Quill } from '@vueup/vue-quill';
import * as Sentry from "@sentry/vue";

// Views
import AddressBook from '@/views/dashboard/AddressBook.vue';
import Configuration from '@/views/Configuration.vue';
import Menus from '@/views/Menus.vue';
import Orders from '@/views/Orders.vue';

// utility
import upperFirst from 'lodash/upperFirst';
import camelCase from 'lodash/camelCase';


/**
 * First we will load all of this project's JavaScript dependencies which
 * includes Vue and other libraries. It is a great starting point when
 * building robust, powerful web applications using Vue and Laravel.
 */
require('./bootstrap');

const icons = Quill.import('ui/icons');
//icons['header']['1'] =
//  '<svg width="13" height="16" viewBox="0 0 13 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4.41541 15.596C3.73275 15.596 3.11408 15.4787 2.55941 15.244C2.00475 15.0093 1.56741 14.6307 1.24741 14.108C0.927414 13.5853 0.767414 12.892 0.767414 12.028V4.348C0.767414 4.156 0.852748 4.01733 1.02341 3.932C1.19408 3.84667 1.39141 3.804 1.61541 3.804C1.81808 3.804 2.01008 3.84667 2.19141 3.932C2.37275 4.01733 2.46341 4.156 2.46341 4.348V12.028C2.46341 12.732 2.63408 13.2547 2.97541 13.596C3.31675 13.9267 3.79675 14.092 4.41541 14.092C5.02341 14.092 5.50341 13.9267 5.85541 13.596C6.20741 13.2547 6.38341 12.732 6.38341 12.028V4.348C6.38341 4.156 6.46875 4.01733 6.63941 3.932C6.81008 3.84667 7.00741 3.804 7.23141 3.804C7.43408 3.804 7.62608 3.84667 7.80741 3.932C7.98875 4.01733 8.07941 4.156 8.07941 4.348V12.028C8.07941 12.892 7.91408 13.5853 7.58341 14.108C7.26341 14.6307 6.82608 15.0093 6.27141 15.244C5.71675 15.4787 5.09808 15.596 4.41541 15.596ZM5.51941 2.22C5.31675 2.22 5.14608 2.14533 5.00741 1.996C4.86875 1.84667 4.79941 1.67067 4.79941 1.468C4.79941 1.26533 4.86875 1.09467 5.00741 0.955999C5.14608 0.806666 5.31675 0.731999 5.51941 0.731999C5.72208 0.731999 5.89275 0.806666 6.03141 0.955999C6.17008 1.09467 6.23941 1.26533 6.23941 1.468C6.23941 1.67067 6.17008 1.84667 6.03141 1.996C5.89275 2.14533 5.72208 2.22 5.51941 2.22ZM3.29541 2.22C3.09275 2.22 2.91675 2.14533 2.76741 1.996C2.62875 1.84667 2.55941 1.67067 2.55941 1.468C2.55941 1.26533 2.62875 1.09467 2.76741 0.955999C2.91675 0.806666 3.09275 0.731999 3.29541 0.731999C3.49808 0.731999 3.66341 0.806666 3.79141 0.955999C3.93008 1.09467 3.99941 1.26533 3.99941 1.468C3.99941 1.67067 3.93008 1.84667 3.79141 1.996C3.66341 2.14533 3.49808 2.22 3.29541 2.22ZM11.8819 15.5C11.7486 15.5 11.6252 15.47 11.5119 15.41C11.4052 15.35 11.3519 15.2633 11.3519 15.15V9.59L11.0019 10.03C10.9619 10.0633 10.9219 10.09 10.8819 10.11C10.8486 10.1233 10.8152 10.13 10.7819 10.13C10.6752 10.13 10.5886 10.0867 10.5219 10C10.4552 9.90667 10.4219 9.80667 10.4219 9.7C10.4219 9.62 10.4419 9.54333 10.4819 9.47C10.5219 9.39667 10.5719 9.33667 10.6319 9.29L11.6719 8.29C11.7052 8.25667 11.7419 8.23 11.7819 8.21C11.8286 8.19 11.8819 8.18 11.9419 8.18C12.0552 8.18 12.1619 8.21 12.2619 8.27C12.3619 8.33 12.4119 8.41667 12.4119 8.53V15.15C12.4119 15.2633 12.3552 15.35 12.2419 15.41C12.1352 15.47 12.0152 15.5 11.8819 15.5Z" fill="#4B5563"/></svg>';
icons['header']['1'] = '';

const scrollBehavior = (to, from, savedPosition) => {
  if (savedPosition) {
    return savedPosition;
  } else {
    const isHash = to.hash ? to.hash.split('#') : undefined;
    if (isHash) {
      let tmp = setTimeout(() => {
        document.getElementById(isHash[1]).scrollIntoView();
        clearTimeout(tmp);
      });
    } else {
      let tmp = setTimeout(() => {
        document.body.scrollTop = 0; // For Safari
        document.documentElement.scrollTop = 0;
        clearTimeout(tmp);
      });
    }
    return null;
  }
};

const router = createRouter({
  // 4. Provide the history implementation to use. We are using the hash history for simplicity here.
  history: createWebHistory(),
  routes, // short for `routes: routes`
  scrollBehavior,
});

router.beforeEach(async (to, from, next) => {
  const menuid = to.params.menuid;
  if (!menuid && to.meta.disabled) {
    return next({ name: 'layout', params: { menuid: to.params?.menuid } });
  }
  return next();
});

const app = createApp({
  components: {
    'address-book': AddressBook,
    configuration: Configuration,
    menus: Menus,
    orders: Orders,
  },
});

// So kann global in Vue-Komponenten z.B. {{ trans('auth.failed') }} aufgerufen werden
app.mixin({
  methods: {
    trans(key) {
      return window.Lang.get(key);
    },
  },
});

const requireComponent = require.context(
  // The relative path of the components folder
  './components',
  // Whether or not to look in subfolders
  false,
  // The regular expression used to match base component filenames
  /Base[A-Z]\w+\.(vue|js)$/
);

requireComponent.keys().forEach((fileName) => {
  // Get component config
  const componentConfig = requireComponent(fileName);

  // Get PascalCase name of component
  const componentName = upperFirst(
    camelCase(
      // Gets the file name regardless of folder depth
      fileName
        .split('/')
        .pop()
        .replace(/\.\w+$/, '')
    )
  );
  // Register component globally
  app.component(
    componentName,
    // Look for the component options on `.default`, which will
    // exist if the component was exported with `export default`,
    // otherwise fall back to module's root.
    componentConfig.default || componentConfig
  );
});

document.addEventListener("DOMContentLoaded", function() {
    var viewportMeta = document.querySelector('meta[name="viewport"]');
    if (window.innerWidth < 1248) {
        viewportMeta.setAttribute('content', 'width=1248');
    } else {
        viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1');
    }
});


const SENTRY_CONSENT_GROUP = 'C0002';
let sentryInitialized = false;

function tryInitSentry() {
  if (sentryInitialized) return;
  const groups = window.OptanonActiveGroups || '';
  if (!groups.includes(SENTRY_CONSENT_GROUP)) return;

  Sentry.init({
    app,
    dsn: process.env.MIX_SENTRY_DSN_PUBLIC,
    environment: process.env.MIX_SENTRY_ENVIRONMENT || 'local',
    integrations: [
      new Sentry.BrowserTracing({
        routingInstrumentation: Sentry.vueRouterInstrumentation(router),
      }),
      new Sentry.Replay({
        maskAllText: true,
        blockAllMedia: true,
        maskAllInputs: true,
      }),
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
  sentryInitialized = true;
}

window.OptanonWrapper = tryInitSentry;
tryInitSentry();

app.use(router).mount('#app');
